// Read/write for the `system/infra-params` Metadata row: the install's real
// bicep suffix + params.
//
// WRITERS: `m8t platform enable-auto-update` (the authoritative write — it has
// every param in hand) and the converge engine's re-stamp, which refreshes the
// existing row's imageRef. `m8t deploy` does NOT write this row.
//
// The updater job READS this row to run an infra converge without guessing the
// suffix — never reconstruct these params from scratch, read the row.
import { TableClient } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import { type InfraParams, entityToInfraParams, infraParamsToEntity, INFRA_PARAMS_PK, INFRA_PARAMS_RK } from "@m8t-stack/platform-release";
import { runAz } from "./az.js";
import { discoverStampStorage } from "./platform-storage-discovery.js";
import { upsertMetadataEntity } from "./metadata-table-write.js";

/** Resolve a `TableClient` for the `Metadata` table that holds `system/infra-params` — same
 *  table + discovery as the stamp/apply-request rows (see platform-stamp.ts / apply-request-store.ts). */
export async function openInfraParamsTable(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
}): Promise<TableClient> {
  const { tableEndpoint } = await discoverStampStorage(opts);
  return new TableClient(tableEndpoint, "Metadata", opts.credential);
}

export async function readInfraParams(client: TableClient): Promise<InfraParams | null> {
  try {
    return entityToInfraParams(await client.getEntity<{ value: string }>(INFRA_PARAMS_PK, INFRA_PARAMS_RK));
  } catch {
    return null;
  }
}

/**
 * Write the row through the shared Metadata-table auth ladder. The installer's
 * managed identity holds Owner at subscription scope but no Storage Table data
 * role, so a bare AAD client 403s on a from-zero install — the ladder's
 * shared-key rung is what makes the install-time write land.
 *
 * `retryHint` is required, not defaulted, for the same reason
 * `upsertMetadataEntity`'s own `retryHint` is required (see
 * metadata-table-write.ts): this function has two reachable callers —
 * `m8t platform enable-auto-update`'s backfill and the converge engine's
 * re-stamp (reachable from `m8t platform update`) — and each needs to point
 * a founder back at the command they actually ran. A default here is exactly
 * how a wrong-for-one-caller remedy would ship silently again.
 */
export async function writeInfraParams(
  ctx: { credential: TokenCredential; subscriptionId: string; resourceGroup: string; retryHint: string; onProgress?: (m: string) => void },
  p: InfraParams,
): Promise<void> {
  await upsertMetadataEntity({
    credential: ctx.credential,
    subscriptionId: ctx.subscriptionId,
    resourceGroup: ctx.resourceGroup,
    entity: infraParamsToEntity(p),
    label: "infra-params row",
    errorCode: "PLATFORM_INFRA_PARAMS_NO_ACCESS",
    retryHint: ctx.retryHint,
    ...(ctx.onProgress ? { onProgress: ctx.onProgress } : {}),
  });
}

/**
 * Fallback ONLY when the `system/infra-params` row is absent: derive the
 * suffix from the gateway container app name (`m8t-gateway-<suffix>`) and
 * VERIFY it by confirming the matching Container Apps Environment
 * (`m8t-cae-<suffix>`) also exists. Refuses to guess on ANY ambiguity — zero
 * or more than one gateway match, or a suffix whose CAE doesn't verify —
 * returning null rather than risk provisioning a duplicate stack.
 */
export async function deriveSuffixVerified(opts: {
  credential?: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
}): Promise<string | null> {
  const list = JSON.parse(
    await runAz([
      "containerapp",
      "list",
      "-g",
      opts.resourceGroup,
      "--subscription",
      opts.subscriptionId,
      "--query",
      "[?starts_with(name, 'm8t-gateway-')].name",
      "-o",
      "json",
    ]),
  ) as string[];
  if (list.length !== 1) return null; // 0 or >1 → refuse to guess
  const suffix = list[0].replace(/^m8t-gateway-/, "");
  // Verify the suffix by confirming the matching CAE exists too.
  const cae = JSON.parse(
    await runAz([
      "containerapp",
      "env",
      "list",
      "-g",
      opts.resourceGroup,
      "--subscription",
      opts.subscriptionId,
      "--query",
      `[?name=='m8t-cae-${suffix}'].name`,
      "-o",
      "json",
    ]),
  ) as string[];
  return cae.length === 1 ? suffix : null;
}
